<script setup lang="ts">
import IcClose from '@/components/Icons/IcClose.vue'
import IcEmptyBox from '@/components/Icons/IcEmptyBox.vue'
import AnalysisConfigurator from '@/components/Registers/AnalysisConfigurator.vue'
import AnalysisSelector from '@/components/Registers/AnalysisSelector.vue'
import RegisterChart from '@/components/Registers/Body/RegisterChart.vue'
import CorrelationAnalysisConfigurator from '@/components/Registers/CorrelationAnalysis/CorrelationAnalysisConfigurator.vue'
import RegistersHeader from '@/components/Registers/Header/RegistersHeader.vue'
import { getSensorColor, translateSensorType } from '@/interfaces/enums.ts'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { CyclePeriod } from '@/services/cycle-analysis.service'
import type { ISensor } from '@/interfaces/entities/sensor'
import { useRegisterStore } from '@/store/register'
import type { IRegisterChartSensorData } from '@/utils/components/register-chart'
import { formatDateWithTime } from '@/utils/date/date-time'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const t = useI18n().t

// Store
const { getEnergyReading } = storeToRefs(useRegisterStore())
const { removeSensor } = useRegisterStore()
const registerStore = useRegisterStore()
const router = useRouter()

const toggleAnalysisComponent = (sensorData: any) => {
  if (!sensorData || sensorData.index === undefined) return

  const sensorIndex = sensorData.index
  try {
    if (registerStore.isAnalysisExpanded(sensorIndex)) {
      registerStore.toggleAnalysisMinimization(sensorIndex)
    } else {
      registerStore.initializeAnalysis(sensorIndex, sensorData)
    }
  } catch {
    // Erro ao alternar componente de análise
  }
}

const isSensorExpanded = (sensorIndex: number): boolean => {
  try {
    return registerStore.isAnalysisExpanded(sensorIndex)
  } catch {
    return false
  }
}

const isSensorMinimized = (sensorIndex: number): boolean => {
  try {
    return registerStore.isAnalysisMinimized(sensorIndex)
  } catch {
    return false
  }
}

const isSelectorOpenForSensor = (sensorIndex: number): boolean => {
  try {
    return registerStore.isSelectorOpen(sensorIndex)
  } catch {
    return false
  }
}

const isConfiguratorOpenForSensor = (sensorIndex: number): boolean => {
  try {
    return registerStore.isConfiguratorOpen(sensorIndex)
  } catch {
    return false
  }
}

const getSelectedAnalysisType = (sensorIndex: number): string | null => {
  try {
    return registerStore.getSelectedAnalysisType(sensorIndex)
  } catch {
    return null
  }
}

const isCycleConfiguratorOpen = (sensorIndex: number): boolean => {
  return isConfiguratorOpenForSensor(sensorIndex) && getSelectedAnalysisType(sensorIndex) === 'cycle_analysis'
}

const isCorrelationConfiguratorOpen = (sensorIndex: number): boolean => {
  return isConfiguratorOpenForSensor(sensorIndex) && getSelectedAnalysisType(sensorIndex) === 'correlation_analysis'
}

const getCorrelationSecondaryForSensor = (sensorIndex: number): IRegisterChartSensorData | null => {
  try {
    return registerStore.getCorrelationSecondaryData(sensorIndex)
  } catch {
    return null
  }
}

const getCorrelationSensorsForSensor = (sensorIndex: number): IRegisterChartSensorData[] => {
  try {
    return registerStore
      .getCorrelationSensors(sensorIndex)
      .map((entry) => entry.data)
      .filter((data): data is IRegisterChartSensorData => data != null)
  } catch {
    return []
  }
}

const getCurrentSensorData = (sensorIndex: number) => {
  const analysis = registerStore.getSensorAnalysis(sensorIndex)
  return analysis?.sensorData || null
}

// Função para obter data inicial do sensor atual
const getCurrentStartDate = (sensorIndex: number) => {
  const analysis = registerStore.getSensorAnalysis(sensorIndex)
  return analysis?.startDate || null
}

// Função para obter data final do sensor atual
const getCurrentEndDate = (sensorIndex: number) => {
  const analysis = registerStore.getSensorAnalysis(sensorIndex)
  return analysis?.endDate || null
}

// Função para prosseguir para análise específica
const proceedToAnalysis = (sensorIndex: number, analysis: string) => {
  registerStore.proceedToAnalysis(sensorIndex, analysis)
}

// Função para voltar ao seletor
const backToSelector = (sensorIndex: number) => {
  registerStore.backToSelector(sensorIndex)
}

// Função para fechar completamente o componente de análise
const closeAnalysisCompletely = (sensorIndex: number) => {
  registerStore.clearAnalysis(sensorIndex)
}

// Função para atualizar períodos dos ciclos para um sensor específico
const updateCyclePeriods = (sensorIndex: number, periods: CyclePeriod[]) => {
  // Os períodos já foram atualizados na store pela função executeAnalysis
  // Não precisamos chamar setAnalysisResults novamente para evitar loop infinito
}

// Função para obter períodos de um sensor específico
const getCyclePeriodsForSensor = (sensorData: any): CyclePeriod[] => {
  if (!sensorData || sensorData.index === undefined) {
    return []
  }

  const results = registerStore.getAnalysisResults(sensorData.index)
  return results?.periods || []
}

const openSensorDetails = (sensor: ISensor) => {
  router.push({ name: 'sensor-detail', params: { id: String(sensor.id) } })
}
</script>

<template>
  <DefaultLayout>
    <div id="registers-view-container">
      <RegistersHeader />

      <template v-if="getEnergyReading.length">
      <div v-for="energySensorItem in getEnergyReading" :key="energySensorItem.index" class="flex flex-col p-6 lg:px-8">
        <div class="flex items-center justify-between text-center">
          <div class="flex flex-col items-start">
            <div class="flex flex-row items-start">
              <h1 class="text-title-md2 font-semibold text-black">
                {{ energySensorItem.sensor.name }}
              </h1>
              <span :class="`ml-4 p-2 py-1 text-sm font-medium text-white ${getSensorColor(energySensorItem.sensor.sensor_type)}`">
                {{ translateSensorType(energySensorItem.sensor.sensor_type) }}
              </span>
              <span
                @click="openSensorDetails(energySensorItem.sensor)"
                class="ml-4 cursor-pointer bg-emerald-700 p-2 py-1 text-sm font-medium text-white hover:bg-emerald-900">
                Detalhes
              </span>
              <div class="ml-4 flex flex-row items-center">
                <button
                  @click="toggleAnalysisComponent(energySensorItem)"
                  class="cursor-pointer bg-yellow-600 p-2 py-1 text-sm font-medium text-white hover:bg-yellow-700">
                  {{ isSensorMinimized(energySensorItem.index) ? '+ Análises' : '- Análises' }}
                </button>
                <div
                  @click="closeAnalysisCompletely(energySensorItem.index)"
                  v-if="isSensorExpanded(energySensorItem.index) && isSensorMinimized(energySensorItem.index)"
                  class="bg-blue-50">
                  <div class="flex items-center space-x-2 bg-red p-2 py-1">
                    <button class="text-sm font-medium text-white" title="Fechar análise completamente">✕</button>
                  </div>
                </div>
              </div>
            </div>

            <p class="text-2xs text-left font-medium text-body">
              {{ formatDateWithTime(energySensorItem.start_date) }} até {{ formatDateWithTime(energySensorItem.end_date) }}
            </p>
          </div>
          <button @click="removeSensor(energySensorItem.index)">
            <IcClose class="hover:opacity-75" />
          </button>
        </div>

        <!-- Indicador de análise em andamento quando minimizado -->
        <div class="h-4"></div>

        <AnalysisSelector
          v-if="
            energySensorItem &&
            isSensorExpanded(energySensorItem.index) &&
            !isSensorMinimized(energySensorItem.index) &&
            isSelectorOpenForSensor(energySensorItem.index)
          "
          :is-open="isSelectorOpenForSensor(energySensorItem.index)"
          :sensor-data="getCurrentSensorData(energySensorItem.index)"
          @close="closeAnalysisCompletely(energySensorItem.index)"
          @proceed-to-analysis="proceedToAnalysis(energySensorItem.index, $event)" />

        <AnalysisConfigurator
          v-if="
            energySensorItem &&
            isSensorExpanded(energySensorItem.index) &&
            !isSensorMinimized(energySensorItem.index) &&
            isCycleConfiguratorOpen(energySensorItem.index)
          "
          :is-open="isCycleConfiguratorOpen(energySensorItem.index)"
          :sensor-data="getCurrentSensorData(energySensorItem.index)"
          :start-date="getCurrentStartDate(energySensorItem.index)"
          :end-date="getCurrentEndDate(energySensorItem.index)"
          :sensor-index="energySensorItem.index"
          @close="closeAnalysisCompletely(energySensorItem.index)"
          @back="backToSelector(energySensorItem.index)"
          @update-periods="updateCyclePeriods(energySensorItem.index, $event)" />

        <CorrelationAnalysisConfigurator
          v-if="
            energySensorItem &&
            isSensorExpanded(energySensorItem.index) &&
            !isSensorMinimized(energySensorItem.index) &&
            isCorrelationConfiguratorOpen(energySensorItem.index)
          "
          :is-open="isCorrelationConfiguratorOpen(energySensorItem.index)"
          :sensor-data="getCurrentSensorData(energySensorItem.index)"
          :sensor-index="energySensorItem.index"
          @close="closeAnalysisCompletely(energySensorItem.index)"
          @back="backToSelector(energySensorItem.index)" />

        <RegisterChart
          v-if="energySensorItem"
          :sensorData="energySensorItem"
          :cyclePeriods="getCyclePeriodsForSensor(energySensorItem)"
          :sensor-index="energySensorItem.index"
          :correlation-sensors="getCorrelationSensorsForSensor(energySensorItem.index)"
          :secondary-sensor-data="getCorrelationSecondaryForSensor(energySensorItem.index)" />
      </div>
      </template>

      <div v-else class="flex flex-col items-center text-center">
        <IcEmptyBox class="h-90 pb-12 pt-24" />
        <p class="text-gray font-satoshi text-title-xsm font-bold">
          {{ t('dashboard.body.totalizers.cards.no_data') }}
        </p>
        <p class="font-satoshitext-title-xs text-gray font-medium">Faça uma busca com o filtro acima e veja os resultados</p>
      </div>
    </div>
  </DefaultLayout>
</template>
